/// <reference path="koyori.ts" />

/**
 * スクロールを扱うコンポーネントです。
 * このエレメントがクリックされた際に対象の位置までスクロールします。
 * <dl>
 * <dt>属性</dt>
 * <dd>target: 対象となるエレメントのセレクタ</dd>
 * <dd>target-in: 対象エレメント全体が画面に表示されている場合に設定される</dd>
 * <dd>position-x: スクロール完了時の対象エレメントの表示位置（autoもしくは省略: 画面に表示された時点で終了, ??%: 割合での位置指定, ??px: ピクセルでの位置指定）</dd>
 * <dd>position-y: スクロール完了時の対象エレメントの表示位置（autoもしくは省略: 画面に表示された時点で終了, ??%: 割合での位置指定, ??px: ピクセルでの位置指定）</dd>
 * </dl>
 * TODO: 連続でイベントを発生させた場合に画面がちらつく（前回のイベントをキャンセルさせる）
 */
class KoyoriScroll extends KoyoriElement {
	
	// setTimeoutのIDのリスト
	static timeouts: NodeJS.Timer[] = [];

	// 対象エレメント
	target: HTMLElement;

	// 対象の親エレメント
	parent: Element;

	// target-in属性の有無
	isIn: boolean = false;

	connectedCallback(): void {
		super.connectedCallback();
		
		this.target = <HTMLElement>document.querySelector(this.getAttribute('target'));
		if (this.target == null) {
			throw 'Illegat attribute "target"';
		}

		let self = this;
		this.parent = this.target.offsetParent;
		if (this.parent == null || this.parent.tagName == 'BODY') {
			this.parent = window.document.body;
		}
		this.parent.addEventListener('scroll', (event: UIEvent) => {
			self.scrolled();
		});
		this.parent.addEventListener('resize', (event: UIEvent) => {
			self.scrolled();
		});
		this.isIn = (this.getAttribute('target-in') != null);
		setTimeout(() => {
			self.scrolled();
		});
		this.addEventListener('click', () => {
			self.clicked();
		});
	}

	private scrolled() {
		let left = this.target.offsetLeft - this.getParentScrollLeft();
		let right = left + this.target.offsetWidth;
		let top = this.target.offsetTop - this.getParentScrollTop();
		let bottom = top + this.target.offsetHeight;

		if (left < 0 || right > this.getParentClientWidth()
			|| top < 0 || bottom > this.getParentClientHeight()) {
			if (this.isIn) {
				this.removeAttribute('target-in');
				this.isIn = false;
			}
		} else {
			if (this.isIn == false) {
				this.setAttribute('target-in', '');
				this.isIn = true;
			}
		}
	}

	private clicked() {
		let positionPattern = new RegExp('^(\\-?[0-9\\.]+)(px|%)$');

		let left = this.target.offsetLeft - this.getParentScrollLeft();
		let right = left + this.target.offsetWidth;
		let top = this.target.offsetTop - this.getParentScrollTop();
		let bottom = top + this.target.offsetHeight;

		let startLeft = this.getParentScrollLeft();
		let dLeft: number = null;
		let xResult = positionPattern.exec(this.getAttribute('position-x') || '');
		if (xResult == null) {
			if (left < 0) {
				dLeft = this.target.offsetLeft - startLeft;
			} else if (right > this.getParentClientWidth()) {
				dLeft = this.target.offsetLeft + this.target.offsetWidth - this.getParentClientWidth() - startLeft;
			}
		} else if (xResult[2] == 'px') {
			dLeft = this.target.offsetLeft - startLeft - parseInt(xResult[1]);
		} else if (xResult[2] == '%') {
			dLeft = this.target.offsetLeft - startLeft - (this.getParentClientWidth() - this.target.offsetWidth) * (parseInt(xResult[1]) / 100);
		}

		let startTop = this.getParentScrollTop();
		let dTop: number = null;
		let yResult = positionPattern.exec(this.getAttribute('position-y') || '');
		if (yResult == null) {
			if (top < 0) {
				dTop = this.target.offsetTop - startTop;
			} else if (bottom > this.getParentClientHeight()) {
				dTop = this.target.offsetTop + this.target.offsetHeight - this.getParentClientHeight() - startTop;
			}
		} else if (yResult[2] == 'px') {
			dTop = this.target.offsetTop - startTop - parseInt(yResult[1]);
		} else if (yResult[2] == '%') {
			dTop = this.target.offsetTop - startTop - (this.getParentClientHeight() - this.target.offsetHeight) * (parseInt(yResult[1]) / 100);
		}

		let self = this;
		KoyoriScroll.timeouts.forEach((id) => {
			clearTimeout(id);
		});
		KoyoriScroll.timeouts = [];
		for (let i = 0; i <= 1; i += 0.01) {
			let id = setTimeout(() => {
				let newLeft = self.parent.scrollLeft;
				if (dLeft != null) {
					newLeft = self.easeInOut(i, startLeft, dLeft);
					self.parent.scrollLeft = newLeft;
				}
				let newTop = self.parent.scrollTop;
				if (dTop != null) {
					newTop = self.easeInOut(i, startTop, dTop);
					self.parent.scrollTop = newTop;
				}
				if (this.parent.tagName == 'BODY') {
					if (newLeft != self.parent.scrollLeft || newTop != self.parent.scrollTop) {
						window.scrollTo(newLeft, newTop);
					}
				}
			}, i * 500);
			KoyoriScroll.timeouts.push(id);
		}
	}

	/**
	 * 親エレメントの左右スクロール量を返します。
	 */
	private getParentScrollLeft(): number {
		return (this.parent.tagName == 'BODY' ? Math.max(this.parent.scrollLeft, window.pageXOffset) : this.parent.scrollLeft);
	}

	/**
	 * 親エレメントの上下スクロール量を返します。
	 */
	private getParentScrollTop(): number {
		return (this.parent.tagName == 'BODY' ? Math.max(this.parent.scrollTop, window.pageYOffset) : this.parent.scrollTop);
	}

	/**
	 * 親エレメントの幅を返します。
	 */
	private getParentClientWidth(): number {
		return (this.parent.tagName == 'BODY' ? Math.min(this.parent.clientWidth, document.documentElement.clientWidth) : this.parent.clientWidth);
	}

	/**
	 * 親エレメントの高さを返します。
	 */
	private getParentClientHeight(): number {
		return (this.parent.tagName == 'BODY' ? Math.min(this.parent.clientHeight, document.documentElement.clientHeight) : this.parent.clientHeight);
	}

	/**
	 * アニメーション用の計算関数です。
	 * 
	 * t: 進行度（0～1）
	 * b: 開始の値
	 * c: 開始と終了の値の差分
	 */
	private easeInOut(t: number, b: number, c: number): number {
		t *= 2;
		if (t < 1) {
			return c / 2.0 * t * t * t + b;
		} else {
			t -= 2;
			return c / 2.0 * (t * t * t + 2) + b;
		}
	}

}
window.customElements.define('koy-scroll', KoyoriScroll);